import * as Sentry from "@sentry/react";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import * as gridLocales from "@mui/x-data-grid";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { muiTheme, externalFonts } from "./muiTheme";
import "./types/gigya";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import "./i18n/config";
import App from "./App";

const locale = "enUS";
const coreLocale = coreLocales[locale];
const gridLocale = gridLocales[locale];

Sentry.setTag("locale", process.env.REACT_APP_LOCALE || "en-us");
Sentry.init({
  dsn: `${process.env.SENTRY_ORG}/${process.env.SENTRY_PROJECT}`,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: "system",
    }),
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.REACT_APP_ENVIRONMENT || "dev",
});

void new Promise<void>((r) => {
  window.onGigyaServiceReady = () => {
    r();
  };

  const s = document.createElement("script");
  s.type = "text/javascript";
  s.src = `https://cdns.${process.env.REACT_APP_CDC_DC!}/JS/gigya.js?apikey=${process.env.REACT_APP_CDC_API_KEY!}`;
  document.querySelector("head")!.appendChild(s);
}).then(() => {
  ReactDOM.createRoot(document.getElementById("root-team-management")!).render(
    <React.StrictMode>
      <Suspense>
        <ThemeProvider theme={createTheme(muiTheme, coreLocale, gridLocale)}>
          <HelmetProvider>
            <div>
              <Helmet>
                {externalFonts.map((f) => (
                  <link key={f} href={f} rel="stylesheet" />
                ))}
              </Helmet>
            </div>
          </HelmetProvider>

          <CssBaseline />
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </Suspense>
    </React.StrictMode>,
  );
});
